import { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import Sidebar from '../../components/feature/Sidebar';
import OfflineIndicator from '../../components/base/OfflineIndicator';
import { Toast } from '../../components/base/Toast';
import { api } from '../../services/api';
import { formatPrice } from '../../utils/format';
import { useBranding } from '../../context/BrandingContext';
import { checkPermission } from '../../utils/permissions';

interface UserInfo { id: string; nom: string; role: string; }

export default function RapportPersonnelDetail() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { branding } = useBranding();
  
  const [user, setUser] = useState<UserInfo | null>(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [personnel, setPersonnel] = useState<any>(null);
  const [history, setHistory] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  
  // Payroll management states
  const [activeDetailTab, setActiveDetailTab] = useState<'taches' | 'reglements'>('taches');
  const [paymentHistory, setPaymentHistory] = useState<any[]>([]);

  // Payment Form States
  const [payDebut, setPayDebut] = useState('');
  const [payFin, setPayFin] = useState('');
  const [payMontant, setPayMontant] = useState(0);
  const [payNotes, setPayNotes] = useState('');

  // Initialize dates from query string or default to current month
  const [dateDebut, setDateDebut] = useState(() => {
    return searchParams.get('debut') || (() => {
      const now = new Date();
      const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
      return firstDay.toISOString().split('T')[0];
    })();
  });

  const [dateFin, setDateFin] = useState(() => {
    return searchParams.get('fin') || (() => {
      return new Date().toISOString().split('T')[0];
    })();
  });

  useEffect(() => {
    const token = localStorage.getItem('atelier_bassoum_token');
    const stored = localStorage.getItem('atelier_bassoum_user');
    if (stored && token) {
      setUser(JSON.parse(stored));
      loadData(token);
    } else {
      navigate('/login');
    }
  }, [navigate, id]);

  const loadPaymentHistory = async (token: string) => {
    try {
      const allHistory = await api.getPaieHistory(token);
      const myHistory = allHistory.filter((item: any) => Number(item.personnel_id) === Number(id));
      setPaymentHistory(myHistory);
    } catch (err) {
      console.error("Erreur historique paiements:", err);
    }
  };

  const loadData = async (token: string, debutStr = dateDebut, finStr = dateFin) => {
    if (!id) return;
    setLoading(true);
    try {
      const [pData, hData] = await Promise.all([
        api.getPersonnelById(token, id),
        api.getPersonnelHistory(token, id, debutStr, finStr)
      ]);
      setPersonnel(pData);
      setHistory(hData);
      await loadPaymentHistory(token);
    } catch (err) {
      setToast({ message: 'Erreur lors du chargement des données', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    const token = localStorage.getItem('atelier_bassoum_token');
    if (token) {
      // Sync date params to URL
      setSearchParams({ debut: dateDebut, fin: dateFin });
      loadData(token, dateDebut, dateFin);
    }
  };

  const hasAccessPaieVoir = checkPermission('paie_voir');
  const hasAccessPaieValider = checkPermission('paie_valider');

  // KPI Calculations
  const totalTasksAssigned = history.length;
  const completedTasks = history.filter(h => h.statut === 'TERMINE');
  const totalTasksCompleted = completedTasks.length;
  const totalPiecesCompleted = completedTasks.reduce(
    (sum, t) => sum + (parseInt(t.quantite) || 0), 0
  );
  
  const completionRate = totalTasksAssigned > 0 
    ? Math.round((totalTasksCompleted / totalTasksAssigned) * 100) 
    : 0;

  const totalGainsValidated = completedTasks.reduce(
    (sum, t) => sum + (parseFloat(t.cout_main_doeuvre) || 0), 0
  );

  const totalGainsPotential = history.reduce(
    (sum, t) => sum + (parseFloat(t.cout_main_doeuvre) || 0), 0
  );

  const avgGainsPerTask = totalTasksCompleted > 0
    ? Math.round(totalGainsValidated / totalTasksCompleted)
    : 0;

  // Unpaid tasks calculations
  const unpaidTasks = history.filter(h => h.statut === 'TERMINE' && !h.paiement_id);
  const unpaidGainsSum = unpaidTasks.reduce((sum, t) => sum + (parseFloat(t.cout_main_doeuvre) || 0), 0);
  const unpaidPiecesSum = unpaidTasks.reduce((sum, t) => sum + (parseInt(t.quantite) || 0), 0);

  const oldestUnpaidTask = unpaidTasks.reduce((oldest: string | null, t) => {
    const dateStr = t.completed_at || t.date_assignation || t.created_at;
    if (!dateStr) return oldest;
    const dStr = dateStr.includes(' ') ? dateStr.split(' ')[0] : dateStr;
    if (!oldest) return dStr;
    return dStr < oldest ? dStr : oldest;
  }, null);

  useEffect(() => {
    if (oldestUnpaidTask) {
      setPayDebut(oldestUnpaidTask);
    } else {
      setPayDebut(dateDebut);
    }
    setPayFin(dateFin);
    setPayMontant(unpaidGainsSum);
  }, [oldestUnpaidTask, unpaidGainsSum, dateDebut, dateFin]);

  const handleSavePayment = async () => {
    if (!payDebut || !payFin || payMontant <= 0) {
      setToast({ message: "Veuillez remplir correctement les dates et le montant.", type: 'error' });
      return;
    }
    const token = localStorage.getItem('atelier_bassoum_token');
    if (!token) return;
    try {
      const res = await api.savePaieRecord(token, {
        personnel_id: Number(id),
        montant_calcule: unpaidGainsSum,
        montant_paye: payMontant,
        date_debut: payDebut,
        date_fin: payFin,
        notes: payNotes
      });
      setToast({ message: res.message || "Paiement enregistré avec succès !", type: 'success' });
      setPayNotes('');
      await loadData(token);
    } catch (err: any) {
      setToast({ message: err.message || "Erreur lors de l'enregistrement", type: 'error' });
    }
  };

  const handleDeletePayment = async (paymentId: number) => {
    if (!window.confirm("Êtes-vous sûr de vouloir annuler ce règlement ? Cela libérera les tâches associées et supprimera la transaction financière.")) return;
    const token = localStorage.getItem('atelier_bassoum_token');
    if (!token) return;
    try {
      const res = await api.deletePaieRecord(token, paymentId);
      setToast({ message: res.message || "Règlement annulé avec succès.", type: 'success' });
      await loadData(token);
    } catch (err: any) {
      setToast({ message: err.message || "Erreur lors de l'annulation", type: 'error' });
    }
  };

  const handleGenerateReceipt = async (pay: any) => {
    const token = localStorage.getItem('atelier_bassoum_token');
    if (!token) return;
    setToast({ message: 'Génération de la fiche de paie...', type: 'success' });
    try {
      const tasksForSlip = await api.getPersonnelHistory(token, id!, pay.date_debut, pay.date_fin);
      const paidTasks = tasksForSlip.filter((t: any) => Number(t.paiement_id) === Number(pay.id));
      const doc = new jsPDF();
      
      // Header branding
      doc.setFont('Helvetica', 'bold');
      doc.setFontSize(20);
      doc.setTextColor(40, 92, 73);
      doc.text('ATELIER BASSOUM', 14, 20);
      
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      doc.setFont('Helvetica', 'normal');
      doc.text('Couture, Mesure & Confection Premium', 14, 25);
      doc.text('Bamako, Mali', 14, 30);
      
      doc.setDrawColor(200, 200, 200);
      doc.line(14, 34, 196, 34);
      
      // Title
      doc.setFont('Helvetica', 'bold');
      doc.setFontSize(14);
      doc.setTextColor(30, 30, 30);
      doc.text("FICHE DE REGLEMENT DE MAIN D'ŒUVRE", 14, 44);
      
      // Grid info
      doc.setFontSize(9);
      doc.setFont('Helvetica', 'normal');
      doc.text(`Employe : ${personnel?.nom}`, 14, 52);
      doc.text(`Specialite : ${personnel?.specialite || personnel?.role || 'Personnel'}`, 14, 58);
      doc.text(`Periode de paie : Du ${formatDateFr(pay.date_debut)} au ${formatDateFr(pay.date_fin)}`, 14, 64);
      
      doc.text(`Date de paiement : ${formatDateFr(pay.date_paiement)}`, 120, 52);
      doc.text(`Reglement Ref : #PAIE-${pay.id}`, 120, 58);
      doc.text(`Valide par : ${pay.admin_nom || 'Gerant'}`, 120, 64);
      
      const tableHeaders = [['Commande', 'Client', 'Modele / Article', 'Tache', 'Qte', 'Tarif U. (FCFA)', 'Total (FCFA)']];
      const tableRows = paidTasks.map((t: any) => {
        const qty = parseInt(t.quantite) || 1;
        const total = parseFloat(t.cout_main_doeuvre) || 0;
        const unit = total / qty;
        return [
          t.commande_code || `CMD-${t.commande_id}`,
          t.client_nom || '-',
          t.article_nom || 'Sur mesure',
          t.tache,
          qty.toString(),
          formatPrice(unit, false),
          formatPrice(total, false)
        ];
      });
      
      autoTable(doc, {
        startY: 72,
        head: tableHeaders,
        body: tableRows,
        styles: { fontSize: 8, cellPadding: 2.5 },
        headStyles: { fillColor: [40, 92, 73] },
        alternateRowStyles: { fillColor: [249, 250, 251] }
      });
      
      const finalY = (doc as any).lastAutoTable.finalY + 10;
      doc.setDrawColor(230, 230, 230);
      doc.setFillColor(250, 250, 250);
      doc.rect(14, finalY, 182, 35, 'F');
      
      doc.setFont('Helvetica', 'bold');
      doc.setFontSize(10);
      doc.text('RECAPITULATIF FINANCIER', 20, finalY + 8);
      
      doc.setFont('Helvetica', 'normal');
      doc.setFontSize(9);
      doc.text(`Montant Brut Calcule : ${formatPrice(pay.montant_calcule)}`, 20, finalY + 16);
      doc.setFont('Helvetica', 'bold');
      doc.text(`Montant Net Regle : ${formatPrice(pay.montant_paye)}`, 20, finalY + 24);
      doc.setFont('Helvetica', 'normal');
      doc.text(`Observations : ${pay.notes || 'Aucune note particuliere.'}`, 110, finalY + 16);
      
      const sigY = finalY + 55;
      doc.setFont('Helvetica', 'bold');
      doc.text("Signature de l'Employe", 30, sigY);
      doc.text("Signature du Gerant / Admin", 130, sigY);
      doc.setDrawColor(180, 180, 180);
      doc.line(30, sigY + 15, 80, sigY + 15);
      doc.line(130, sigY + 15, 180, sigY + 15);
      
      const pdfName = `fiche_paie_${personnel?.nom?.toLowerCase()?.replace(/\s+/g, '_')}_#${pay.id}.pdf`;
      doc.save(pdfName);
      setToast({ message: 'Fiche de paie générée avec succès !', type: 'success' });
    } catch (err: any) {
      console.error(err);
      setToast({ message: 'Erreur lors de la génération de la fiche de paie.', type: 'error' });
    }
  };

  // Filter history by search query
  const filteredHistory = history.filter(item => {
    const q = searchQuery.toLowerCase();
    return (
      !q ||
      (item.commande_code || '').toLowerCase().includes(q) ||
      (item.client_nom || '').toLowerCase().includes(q) ||
      (item.tache || '').toLowerCase().includes(q) ||
      (item.article_nom || '').toLowerCase().includes(q)
    );
  });

  const formatDateFr = (dateStr: string) => {
    if (!dateStr) return '-';
    const datePart = dateStr.includes(' ') ? dateStr.split(' ')[0] : dateStr;
    const parts = datePart.split('-');
    if (parts.length === 3) {
      return `${parts[2]}/${parts[1]}/${parts[0]}`;
    }
    return new Date(dateStr).toLocaleDateString('fr-FR');
  };

  // Export to Excel
  const handleExportExcel = () => {
    if (filteredHistory.length === 0) {
      setToast({ message: 'Aucune donnée à exporter', type: 'error' });
      return;
    }

    const exportRows = filteredHistory.map(item => {
      const qty = parseInt(item.quantite) || 1;
      const totalCost = parseFloat(item.cout_main_doeuvre) || 0;
      const unitCost = totalCost / qty;
      return {
        'Référence Commande': item.commande_code || `CMD-${item.commande_id}`,
        'Client': item.client_nom || 'N/A',
        'Article / Modèle': item.article_nom || item.modele_nom || 'Sur mesure',
        'Tâche': item.tache,
        'Quantité': qty,
        'Tarif Unitaire (FCFA)': unitCost,
        'Coût Main d\'œuvre Total (FCFA)': totalCost,
        'Statut': item.statut,
        'Date assignation': formatDateFr(item.date_assignation || item.created_at),
        'Date validation': item.completed_at ? formatDateFr(item.completed_at) : '-'
      };
    });

    const worksheet = XLSX.utils.json_to_sheet(exportRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Historique Tâches');
    
    // Auto-fit column widths
    const maxLens = Object.keys(exportRows[0]).map(key => {
      const lens = exportRows.map(r => String((r as any)[key] || '').length);
      return Math.max(key.length, ...lens) + 2;
    });
    worksheet['!cols'] = maxLens.map(w => ({ wch: w }));

    const fileName = `rapport_taches_${personnel?.nom?.toLowerCase()?.replace(/\s+/g, '_')}_${dateDebut}_${dateFin}.xlsx`;
    XLSX.writeFile(workbook, fileName);
    setToast({ message: 'Export Excel réussi !', type: 'success' });
  };

  // Export to PDF
  const handleExportPDF = () => {
    if (filteredHistory.length === 0) {
      setToast({ message: 'Aucune donnée à exporter', type: 'error' });
      return;
    }

    const doc = new jsPDF();
    
    // Title & Info
    doc.setFont('Helvetica', 'bold');
    doc.setFontSize(16);
    doc.text(`Rapport d'activité individuel`, 14, 20);
    
    doc.setFontSize(11);
    doc.setFont('Helvetica', 'normal');
    doc.text(`Nom: ${personnel?.nom}`, 14, 28);
    doc.text(`Spécialité: ${personnel?.specialite || personnel?.role || 'Personnel'}`, 14, 34);
    doc.text(`Période: ${formatDateFr(dateDebut)} au ${formatDateFr(dateFin)}`, 14, 40);
    
    // KPI summary table
    autoTable(doc, {
      startY: 46,
      head: [['Tâches Assignées', 'Tâches Terminées', 'Pièces Terminées', 'Taux de Complétion', 'Gains Validés (FCFA)']],
      body: [[
        totalTasksAssigned,
        totalTasksCompleted,
        totalPiecesCompleted,
        `${completionRate}%`,
        formatPrice(totalGainsValidated, false)
      ]],
      theme: 'grid',
      styles: { halign: 'center', cellPadding: 3, fontSize: 9 },
      headStyles: { fillColor: [40, 92, 73], textColor: [255, 255, 255] }
    });

    // History Table
    const headers = [['Réf.', 'Client', 'Modèle', 'Tâche', 'Qte', 'Tarif U. (FCFA)', 'Gain Tot. (FCFA)', 'Statut', 'Fait le']];
    const rows = filteredHistory.map(item => {
      const qty = parseInt(item.quantite) || 1;
      const totalCost = parseFloat(item.cout_main_doeuvre) || 0;
      const unitCost = totalCost / qty;
      return [
        item.commande_code || `#${item.commande_id}`,
        item.client_nom || '-',
        item.article_nom || 'Sur mesure',
        item.tache,
        qty.toString(),
        formatPrice(unitCost, false),
        formatPrice(totalCost, false),
        item.statut,
        item.completed_at ? formatDateFr(item.completed_at) : '-'
      ];
    });

    autoTable(doc, {
      startY: (doc as any).lastAutoTable.finalY + 10,
      head: headers,
      body: rows,
      styles: { fontSize: 8, cellPadding: 2.5 },
      headStyles: { fillColor: [79, 70, 229] },
      alternateRowStyles: { fillColor: [249, 250, 251] }
    });

    const fileName = `rapport_taches_${personnel?.nom?.toLowerCase()?.replace(/\s+/g, '_')}_${dateDebut}_${dateFin}.pdf`;
    doc.save(fileName);
    setToast({ message: 'Export PDF réussi !', type: 'success' });
  };

  if (!user) return null;

  return (
    <div className="flex h-screen bg-gray-50 font-nunito overflow-hidden">
      <OfflineIndicator />
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      
      <div className="hidden md:block">
        <Sidebar role={user.role} nom={user.nom} collapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(v => !v)} />
      </div>

      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* Header */}
        <header className="bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center">
            <button 
              onClick={() => navigate(`/rapports?tab=personnel&debut=${dateDebut}&fin=${dateFin}`)} 
              className="p-2 bg-gray-50 text-gray-500 hover:bg-gray-100 hover:text-gray-900 rounded-xl transition-all border border-gray-200/80 mr-4 flex items-center justify-center cursor-pointer"
            >
              <i className="ri-arrow-left-line text-lg" />
            </button>
            <div>
              <h2 className="font-playfair font-bold text-xl text-gray-900">
                {loading ? 'Chargement...' : `Rapport de ${personnel?.nom}`}
              </h2>
              <p className="text-xs text-gray-400 mt-0.5">
                {personnel?.specialite || personnel?.role || 'Personnel'} · Historique & Activité
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <button
              onClick={handleExportExcel}
              className="py-2.5 px-4 bg-green-50 text-green-700 hover:bg-green-600 hover:text-white border border-green-100 rounded-xl text-xs font-bold transition-all flex items-center gap-2 cursor-pointer shadow-sm shadow-green-50"
            >
              <i className="ri-file-excel-2-line text-sm" />
              <span className="hidden sm:inline">Excel</span>
            </button>
            <button
              onClick={handleExportPDF}
              className="py-2.5 px-4 bg-red-50 text-red-700 hover:bg-red-600 hover:text-white border border-red-100 rounded-xl text-xs font-bold transition-all flex items-center gap-2 cursor-pointer shadow-sm shadow-red-50"
            >
              <i className="ri-file-pdf-line text-sm" />
              <span className="hidden sm:inline">PDF</span>
            </button>
          </div>
        </header>

        {/* Date Filter Panel */}
        <section className="bg-white border-b border-gray-100 px-6 py-3 flex-shrink-0">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-xl px-3 py-1.5 focus-within:ring-2 focus-within:ring-foret-500">
                <span className="text-[10px] font-black text-gray-400 uppercase">Du</span>
                <input 
                  type="date" 
                  value={dateDebut} 
                  onChange={e => setDateDebut(e.target.value)} 
                  className="bg-transparent border-none text-xs font-bold text-gray-800 focus:outline-none cursor-pointer"
                />
              </div>
              <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-xl px-3 py-1.5 focus-within:ring-2 focus-within:ring-foret-500">
                <span className="text-[10px] font-black text-gray-400 uppercase">Au</span>
                <input 
                  type="date" 
                  value={dateFin} 
                  onChange={e => setDateFin(e.target.value)} 
                  className="bg-transparent border-none text-xs font-bold text-gray-800 focus:outline-none cursor-pointer"
                />
              </div>
              <button 
                onClick={handleRefresh}
                className="p-2 bg-foret-500 hover:bg-foret-600 text-white rounded-xl transition-all cursor-pointer flex items-center justify-center border border-foret-600"
                title="Filtrer"
              >
                <i className="ri-filter-2-line text-sm" />
              </button>
            </div>
            
            <div className="w-full sm:w-64">
              <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-xl px-3 py-1.5 transition-all focus-within:ring-2 focus-within:ring-foret-500">
                <i className="ri-search-line text-gray-400" />
                <input 
                  type="text" 
                  placeholder="Rechercher une tâche, client..." 
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="bg-transparent border-none text-xs text-gray-800 focus:outline-none w-full"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Scrollable Dashboard Body */}
        <main className="flex-1 overflow-y-auto p-6 space-y-6">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-32">
              <div className="w-10 h-10 border-4 border-foret-200 border-t-foret-600 rounded-full animate-spin mb-4" />
              <p className="text-gray-400 text-sm">Chargement des données...</p>
            </div>
          ) : (
            <>
              {/* KPIs Row */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  {
                    label: "Gains Validés",
                    value: formatPrice(totalGainsValidated),
                    sub: `Sur un potentiel de ${formatPrice(totalGainsPotential)}`,
                    icon: "ri-wallet-3-line",
                    color: "text-green-600 bg-green-50 border-green-100"
                  },
                  {
                    label: "Taux de Complétion",
                    value: `${completionRate}%`,
                    sub: `${totalTasksCompleted} finies sur ${totalTasksAssigned} assignées`,
                    icon: "ri-percent-line",
                    color: "text-foret-600 bg-foret-50 border-foret-100"
                  },
                  {
                    label: "Articles Terminés",
                    value: `${totalPiecesCompleted} pcs`,
                    sub: `${totalTasksCompleted} tâches validées par l'atelier`,
                    icon: "ri-checkbox-circle-line",
                    color: "text-blue-600 bg-blue-50 border-blue-100"
                  },
                  {
                    label: "Moyenne par Tâche",
                    value: formatPrice(avgGainsPerTask),
                    sub: "Gains moyens par tâche finie",
                    icon: "ri-calculator-line",
                    color: "text-purple-600 bg-purple-50 border-purple-100"
                  }
                ].map((kpi, idx) => (
                  <div key={idx} className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm flex flex-col justify-between">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-[11px] font-black text-gray-400 uppercase tracking-widest">{kpi.label}</span>
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-base border ${kpi.color}`}>
                        <i className={kpi.icon} />
                      </div>
                    </div>
                    <div>
                      <p className="text-xl font-extrabold text-gray-900">{kpi.value}</p>
                      <p className="text-[10px] text-gray-400 mt-1 font-bold">{kpi.sub}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Tab Selector */}
              {hasAccessPaieVoir && (
                <div className="flex gap-2 border-b border-gray-200 pb-1">
                  <button
                    onClick={() => setActiveDetailTab('taches')}
                    className={`pb-2 px-4 text-xs font-bold border-b-2 transition-all cursor-pointer ${
                      activeDetailTab === 'taches'
                        ? 'border-foret-600 text-foret-600'
                        : 'border-transparent text-gray-400 hover:text-gray-600'
                    }`}
                  >
                    Historique des Tâches ({filteredHistory.length})
                  </button>
                  <button
                    onClick={() => setActiveDetailTab('reglements')}
                    className={`pb-2 px-4 text-xs font-bold border-b-2 transition-all cursor-pointer ${
                      activeDetailTab === 'reglements'
                        ? 'border-foret-600 text-foret-600'
                        : 'border-transparent text-gray-400 hover:text-gray-600'
                    }`}
                  >
                    Règlements & Fiches de paie ({paymentHistory.length})
                  </button>
                </div>
              )}

              {/* Task History Panel */}
              {activeDetailTab === 'taches' && (
                <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm">
                  <div className="px-6 py-4 border-b border-gray-50 flex items-center justify-between">
                    <h3 className="font-playfair font-bold text-sm text-gray-800">Historique des tâches ({filteredHistory.length})</h3>
                    <span className="text-[10px] font-black uppercase bg-gray-100 text-gray-500 px-2.5 py-1 rounded-full">
                      {formatDateFr(dateDebut)} au {formatDateFr(dateFin)}
                    </span>
                  </div>

                  {filteredHistory.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-20 text-center">
                      <div className="w-12 h-12 rounded-2xl bg-gray-50 flex items-center justify-center text-gray-300 mb-4 border border-gray-100">
                        <i className="ri-folder-open-line text-2xl" />
                      </div>
                      <p className="text-sm font-bold text-gray-500">Aucune tâche trouvée</p>
                      <p className="text-xs text-gray-400 mt-1">Aucune activité enregistrée ou correspondant aux filtres pour cette période.</p>
                    </div>
                  ) : (
                    <>
                      {/* Mobile View: Tasks Cards */}
                      <div className="md:hidden divide-y divide-gray-50 pb-20">
                        {filteredHistory.map(item => (
                          <div key={item.id} className="p-4 space-y-3 bg-white">
                            <div className="flex items-center justify-between">
                              <span 
                                onClick={() => navigate(`/commande/${item.commande_id}`)}
                                className="text-[10px] font-black text-gray-400 hover:text-foret-600 transition-colors cursor-pointer border-b border-dashed border-gray-300 animate-pulse"
                              >
                                {item.commande_code || `CMD-#${item.commande_id}`}
                              </span>
                              <div className="flex items-center gap-1.5">
                                <span className={`text-[9px] font-black px-2 py-0.5 rounded-full border ${
                                  item.statut === 'TERMINE' 
                                    ? 'bg-green-50 text-green-700 border-green-150' 
                                    : item.statut === 'EN_COURS' 
                                      ? 'bg-purple-50 text-purple-700 border-purple-150' 
                                      : 'bg-amber-50 text-amber-700 border-amber-150'
                                }`}>
                                  {item.statut}
                                </span>
                                <span className={`text-[9px] font-black px-2 py-0.5 rounded-full border ${
                                  item.paiement_id 
                                    ? 'bg-green-50 text-green-700 border-green-150'
                                    : item.statut === 'TERMINE'
                                      ? 'bg-amber-50 text-amber-700 border-amber-150'
                                      : 'bg-gray-50 text-gray-400 border-gray-150'
                                }`}>
                                  {item.paiement_id ? 'PAYÉ' : item.statut === 'TERMINE' ? 'À RÉGLER' : 'NON VALIDÉ'}
                                </span>
                              </div>
                            </div>
                            <div>
                              <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">{item.client_nom || 'Client inconnu'}</p>
                              <h4 className="text-sm font-black text-gray-900 mt-0.5">{item.tache}</h4>
                              <p className="text-xs text-gray-500 font-bold mt-1 uppercase tracking-tight">{item.article_nom || 'Sur mesure'}</p>
                            </div>
                            <div className="flex items-center justify-between pt-2 border-t border-gray-50 text-[11px] font-bold">
                              <span className="text-gray-400">
                                Tarif: {formatPrice((parseFloat(item.cout_main_doeuvre) || 0) / (parseInt(item.quantite) || 1), false)} | Gain: {formatPrice(item.cout_main_doeuvre)}
                              </span>
                              <span className="text-gray-400">
                                {item.completed_at ? `Validé le : ${formatDateFr(item.completed_at)}` : `Assigné le : ${formatDateFr(item.date_assignation || item.created_at)}`}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Desktop View: Table */}
                      <div className="hidden md:block overflow-x-auto">
                        <table className="w-full text-left">
                          <thead>
                            <tr className="bg-gray-50 border-b border-gray-100">
                              {['Commande', 'Client', 'Article/Modèle', 'Tâche', 'Qte', 'Tarif Unitaire', 'Gain Total', 'Statut', 'Paiement', 'Date Activité'].map(h => (
                                <th key={h} className="text-left text-[10px] font-bold text-gray-400 uppercase px-6 py-3 whitespace-nowrap tracking-wider">{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-50 text-sm">
                            {filteredHistory.map(item => (
                              <tr key={item.id} className="hover:bg-gray-50/50 transition-colors">
                                <td className="px-6 py-3.5">
                                  <button
                                    onClick={() => navigate(`/commande/${item.commande_id}`)}
                                    className="text-xs font-black text-gray-500 hover:text-foret-600 transition-colors border-b border-dashed border-gray-300 hover:border-foret-500 cursor-pointer"
                                  >
                                    {item.commande_code || `CMD-${item.commande_id}`}
                                  </button>
                                </td>
                                <td className="px-6 py-3.5 font-bold text-gray-800">
                                  {item.client_nom || 'Client inconnu'}
                                </td>
                                <td className="px-6 py-3.5">
                                  <span className="text-xs font-bold text-gray-400 uppercase tracking-tight">
                                    {item.article_nom || 'Sur mesure'}
                                  </span>
                                </td>
                                <td className="px-6 py-3.5 font-bold text-gray-750">
                                  {item.tache}
                                </td>
                                <td className="px-6 py-3.5 font-bold text-gray-500">
                                  {item.quantite || 1}
                                </td>
                                <td className="px-6 py-3.5 font-bold text-gray-700">
                                  {formatPrice((parseFloat(item.cout_main_doeuvre) || 0) / (parseInt(item.quantite) || 1), false)}
                                </td>
                                <td className="px-6 py-3.5 font-bold text-foret-700">
                                  {formatPrice(item.cout_main_doeuvre)}
                                </td>
                                <td className="px-6 py-3.5">
                                  <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full border ${
                                    item.statut === 'TERMINE' 
                                      ? 'bg-green-50 text-green-700 border-green-150' 
                                      : item.statut === 'EN_COURS' 
                                        ? 'bg-purple-50 text-purple-700 border-purple-150' 
                                        : 'bg-amber-50 text-amber-700 border-amber-150'
                                  }`}>
                                    {item.statut}
                                  </span>
                                </td>
                                <td className="px-6 py-3.5">
                                  {item.paiement_id ? (
                                    <span className="text-[10px] font-bold px-2.5 py-1 rounded-full bg-green-50 text-green-700 border border-green-200">
                                      PAYÉ
                                    </span>
                                  ) : item.statut === 'TERMINE' ? (
                                    <span className="text-[10px] font-bold px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                                      À RÉGLER
                                    </span>
                                  ) : (
                                    <span className="text-[10px] font-bold px-2.5 py-1 rounded-full bg-gray-50 text-gray-400 border border-gray-150">
                                      NON VALIDÉ
                                    </span>
                                  )}
                                </td>
                                <td className="px-6 py-3.5 text-xs text-gray-400 font-bold">
                                  {item.completed_at 
                                    ? `Fait le: ${formatDateFr(item.completed_at)}` 
                                    : `Assigné: ${formatDateFr(item.date_assignation || item.created_at)}`}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Règlements & Fiches de paie Panel */}
              {activeDetailTab === 'reglements' && hasAccessPaieVoir && (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Left Column: Form to register payment (if has access and has unpaid tasks) */}
                  <div className="lg:col-span-1 space-y-6">
                    <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm space-y-4">
                      <h3 className="font-playfair font-bold text-sm text-gray-800 border-b border-gray-50 pb-3">Enregistrer un Règlement</h3>
                      
                      {unpaidGainsSum > 0 ? (
                        <>
                          <div className="bg-amber-50 border border-amber-100 rounded-xl p-3.5 space-y-1">
                            <p className="text-xs text-amber-800 font-bold">Tâches en attente de règlement</p>
                            <p className="text-xl font-extrabold text-amber-900">{formatPrice(unpaidGainsSum)}</p>
                            <p className="text-[10px] text-amber-700 font-bold uppercase">{unpaidPiecesSum} pièces à régler</p>
                          </div>
                          
                          {hasAccessPaieValider ? (
                            <div className="space-y-3.5">
                              <div>
                                <label className="block text-[10px] font-black text-gray-400 uppercase mb-1">Date de Début</label>
                                <input 
                                  type="date"
                                  value={payDebut}
                                  onChange={e => setPayDebut(e.target.value)}
                                  className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3.5 py-2 text-xs font-bold text-gray-800 focus:outline-none focus:ring-2 focus:ring-foret-500 cursor-pointer"
                                />
                              </div>
                              <div>
                                <label className="block text-[10px] font-black text-gray-400 uppercase mb-1">Date de Fin</label>
                                <input 
                                  type="date"
                                  value={payFin}
                                  onChange={e => setPayFin(e.target.value)}
                                  className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3.5 py-2 text-xs font-bold text-gray-800 focus:outline-none focus:ring-2 focus:ring-foret-500 cursor-pointer"
                                />
                              </div>
                              <div>
                                <label className="block text-[10px] font-black text-gray-400 uppercase mb-1">Montant Payé (FCFA)</label>
                                <input 
                                  type="number"
                                  value={payMontant}
                                  onChange={e => setPayMontant(Number(e.target.value))}
                                  className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3.5 py-2 text-xs font-bold text-gray-800 focus:outline-none focus:ring-2 focus:ring-foret-500"
                                />
                              </div>
                              <div>
                                <label className="block text-[10px] font-black text-gray-400 uppercase mb-1">Notes / Observations</label>
                                <textarea 
                                  value={payNotes}
                                  onChange={e => setPayNotes(e.target.value)}
                                  rows={2}
                                  placeholder="Notes ou détails supplémentaires..."
                                  className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3.5 py-2 text-xs font-bold text-gray-800 focus:outline-none focus:ring-2 focus:ring-foret-500 resize-none"
                                />
                              </div>
                              <button
                                onClick={handleSavePayment}
                                className="w-full py-2.5 bg-foret-500 hover:bg-foret-600 text-white text-xs font-bold rounded-xl transition-all cursor-pointer border border-foret-600 shadow-sm flex items-center justify-center gap-1.5"
                              >
                                <i className="ri-save-line text-sm" /> Enregistrer le règlement
                              </button>
                            </div>
                          ) : (
                            <p className="text-xs text-gray-400 italic">Vous n'avez pas la permission d'enregistrer des règlements.</p>
                          )}
                        </>
                      ) : (
                        <div className="py-6 text-center">
                          <i className="ri-checkbox-circle-fill text-green-500 text-2xl" />
                          <p className="text-xs font-bold text-gray-500 mt-2">Aucune tâche en attente</p>
                          <p className="text-[10px] text-gray-400 mt-0.5">Toutes les tâches validées de ce personnel ont été réglées.</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Right Column: Payments History */}
                  <div className="lg:col-span-2 bg-white p-5 rounded-2xl border border-gray-100 shadow-sm space-y-4">
                    <h3 className="font-playfair font-bold text-sm text-gray-800 border-b border-gray-50 pb-3">Historique des règlements ({paymentHistory.length})</h3>
                    
                    {paymentHistory.length === 0 ? (
                      <div className="py-16 text-center">
                        <i className="ri-history-line text-gray-300 text-3xl" />
                        <p className="text-xs font-bold text-gray-500 mt-2">Aucun règlement enregistré</p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-left text-xs">
                          <thead>
                            <tr className="bg-gray-50 border-b border-gray-100">
                              <th className="px-4 py-2.5 text-gray-400 font-bold uppercase">Date</th>
                              <th className="px-4 py-2.5 text-gray-400 font-bold uppercase">Période</th>
                              <th className="px-4 py-2.5 text-gray-400 font-bold uppercase">Montant</th>
                              <th className="px-4 py-2.5 text-gray-400 font-bold uppercase">Payé par</th>
                              <th className="px-4 py-2.5 text-gray-400 font-bold uppercase text-right">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-50">
                            {paymentHistory.map(pay => (
                              <tr key={pay.id} className="hover:bg-gray-50/50 transition-colors">
                                <td className="px-4 py-3 font-bold text-gray-800">
                                  {formatDateFr(pay.date_paiement)}
                                </td>
                                <td className="px-4 py-3 text-gray-505">
                                  Du {formatDateFr(pay.date_debut)} au {formatDateFr(pay.date_fin)}
                                </td>
                                <td className="px-4 py-3 font-black text-foret-700">
                                  {formatPrice(pay.montant_paye)}
                                </td>
                                <td className="px-4 py-3 text-gray-400 font-medium">
                                  {pay.admin_nom || 'Admin'}
                                </td>
                                <td className="px-4 py-3 text-right flex items-center justify-end gap-1.5">
                                  <button
                                    onClick={() => handleGenerateReceipt(pay)}
                                    className="p-1.5 bg-blue-50 text-blue-600 hover:bg-blue-600 hover:text-white rounded-lg transition-all cursor-pointer border border-blue-100 shadow-sm"
                                    title="Fiche de paie (PDF)"
                                  >
                                    <i className="ri-file-text-line" />
                                  </button>
                                  {hasAccessPaieValider && (
                                    <button
                                      onClick={() => handleDeletePayment(pay.id)}
                                      className="p-1.5 bg-red-50 text-red-600 hover:bg-red-600 hover:text-white rounded-lg transition-all cursor-pointer border border-red-100 shadow-sm"
                                      title="Annuler le règlement"
                                    >
                                      <i className="ri-delete-bin-line" />
                                    </button>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </main>
      </div>
    </div>
  );
}
